/* @HEADER
 * @version 1.0.1469 | 2026-09-17
 * @purpose The decisions behind AuthenticatedApp's in-app navigation history — when a move is recorded, what a snapshot holds, how the stack is capped and popped — pure over plain values so node:test can run them (ZVX-DEV-6015).
 * @sidecar hostNavigationDecisions.mjs.header.md
 * @layer app | @hex domain | @ctx navigation
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-6015, ZVX-DEV-1454

/** The back-stack never grows past this many entries (the oldest falls off). */
export const NAV_HISTORY_CAP = 50;

/**
 * A global-route move is recorded unless it is a no-op: the same route, with
 * the one exception that «tasks» while INSIDE a space is a real move back to
 * the spaces list.
 *
 * @param {{ route: string, target: string, activeSpaceId: string | null }} input
 * @returns {boolean}
 */
export function shouldRecordNavigate({ route, target, activeSpaceId }) {
  const alreadyThere = target === route && !(target === 'tasks' && activeSpaceId !== null);
  return !alreadyThere;
}

/**
 * The snapshot a move leaves behind: where we stood and how far the main
 * column was scrolled.
 *
 * @param {{ route: string, activeSpaceId: string | null, activeView: string, scrollTop: number }} input
 */
export function snapshotOf({ route, activeSpaceId, activeView, scrollTop }) {
  return { route, spaceId: activeSpaceId, view: activeView, scrollTop };
}

/**
 * Push with the cap: keep the newest NAV_HISTORY_CAP entries. Returns a new
 * array; the input is not mutated.
 *
 * @template S
 * @param {S[]} history
 * @param {S} snap
 * @returns {S[]}
 */
export function pushSnapshot(history, snap) {
  return [...history.slice(-(NAV_HISTORY_CAP - 1)), snap];
}

/**
 * Pop for «back»: the snapshot to restore and the stack without it, or
 * `null` when there is nothing to go back to. The input is not mutated.
 *
 * @template S
 * @param {S[]} history
 * @returns {{ snap: S, rest: S[] } | null}
 */
export function popSnapshot(history) {
  if (history.length === 0) return null;
  return { snap: history[history.length - 1], rest: history.slice(0, -1) };
}
